import { FC, useEffect, useState, useRef, useMemo } from 'react'

import get from 'lodash/get'
import { getSessionReport } from 'controllers/main'
import PageContainer from 'shared/components/PageContainer'
import { Proto } from 'types/demo3'
import ViewToggle from '../components/recap/ViewToggle'
import JsonView from '../components/recap/JsonView'
import SessionSummaryCard from '../components/recap/SessionSummaryCard'
import TranscriptSection from '../components/recap/TranscriptSection'
import ShareButton from '../components/recap/ShareButton'
import ReasoningLoader from '../components/recap/ReasoningLoader'
import MetaScoreSummary, {
  StatusBadge,
  computeMetaScore
} from '../components/recap/MetaScoreSummary'
import { PageLoadingSkeleton } from '../components/recap/LoadingStates'
import {
  Sparkles,
  User,
  MapPin,
  Video,
  ChevronDown,
  MessageSquareText
} from 'lucide-react'
import { ReactComponent as FacesignIcon } from '../shared/assets/facesignIcon.svg'
import has from 'lodash/has'
import jsLogger from 'js-logger'
// @ts-ignore - typewriter-effect works with preact
import Typewriter from 'typewriter-effect'
import reasoningStatements from '../data/reasoningStatements.json'
import { CriterionAnalysis, CriterionEvaluation } from '@facesignai/api'
import { useSmartScroll } from '../hooks/useSmartScroll'
import { useRevealPhases, RevealPhase } from '../hooks/useRevealPhases'
import {
  UPDATES_LIMIT,
  TYPEWRITER_DELAY,
  TYPEWRITER_SCROLL_INTERVAL,
  FACESIGN_GREEN,
  TYPEWRITER_CURSOR_COLOR,
  SHIMMER_GRADIENT,
  SHIMMER_DURATION_S,
  INITIAL_POLL_INTERVAL,
  SLOW_POLL_INTERVAL
} from '../constants/sessionReport'
import SessionVideo from 'components/recap/SessionVideo'
import size from 'lodash/size'
import map from 'lodash/map'
import startCase from 'lodash/startCase'

type Props = {
  sessionId: string
  animationOn: boolean
  title?: string
}

const SessionReport: FC<Props> = ({ sessionId, animationOn }) => {
  // Custom hooks
  const {
    isUserScrolledUp,
    hasNewContentBelow,
    scrollToBottom,
    forceScrollToBottom,
    handleScroll,
    scrollContainerRef,
    contentRef
  } = useSmartScroll()

  const { revealPhase, setRevealPhase, isLoadingComplete } = useRevealPhases()
  // Component state
  const [isLoading, setIsLoading] = useState(true)
  const [reportResponse, setReportResponse] =
    useState<Proto.SessionReportResponse | null>(null)
  const [isJsonView, setIsJsonView] = useState(false)
  const [videoDuration, setVideoDuration] = useState<number | null>(null)
  const [videoSubsectionIndex, setVideoSubsectionIndex] = useState(0)
  // Refs
  const reportUpdateTimer = useRef<number>(0)
  const updatesCounter = useRef(0)

  // Memoized header status level
  const headerLevel = useMemo(
    () =>
      reportResponse
        ? computeMetaScore(
            reportResponse.providedUserDetected,
            reportResponse.report?.videoAIAnalysis,
            reportResponse.semanticRiskAnalysis?.riskLevel
          )
        : null,
    [reportResponse]
  )

  // jsLogger.log('revealPhase', revealPhase)

  // // Video analysis sub-sections configuration (memoized)
  // const videoSubsections = useMemo(
  //   () =>
  //     reasoningStatements.videoSubsections.map((section) => ({
  //       key: section.key,
  //       title: section.title,
  //       statements: reasoningStatements.videoAnalysis[
  //         (section.statementsKey ||
  //           section.key) as keyof typeof reasoningStatements.videoAnalysis
  //       ] as string[]
  //     })),
  //   []
  // )

  // Load view preference from localStorage
  useEffect(() => {
    const savedView = localStorage.getItem('recapViewMode')
    if (savedView === 'json') {
      setIsJsonView(true)
    }
  }, [])

  const handleViewToggle = (isJson: boolean) => {
    setIsJsonView(isJson)
    localStorage.setItem('recapViewMode', isJson ? 'json' : 'visual')
  }

  const updateSessionReport = async () => {
    if (updatesCounter.current > UPDATES_LIMIT) {
      jsLogger.log('Update limit reached, stopping further updates.')
      clearTimeout(reportUpdateTimer.current)
      return
    }
    jsLogger.log('Fetching session report...')
    const reportRaw = await getSessionReport(sessionId)
    jsLogger.log('Session report obtained:', reportRaw)
    if (has(reportRaw, 'error')) {
      jsLogger.error('Session report error', reportRaw)
      return
    }
    const r = reportRaw as Proto.SessionReportResponse
    setReportResponse(r)
    updatesCounter.current += 1
    jsLogger.log(`Update count: ${updatesCounter.current}`)

    const userVideoExists = has(r, 'media.videos.user')
    const avatarVideoExists = has(r, 'media.videos.avatar')

    jsLogger.log('[updateSessionReport] userVideoExists:', userVideoExists)
    jsLogger.log('[updateSessionReport] avatarVideoExists:', avatarVideoExists)
    jsLogger.log(
      '[updateSessionReport] r.report?.aiAnalysis:',
      r.report?.aiAnalysis
    )
    jsLogger.log(
      '[updateSessionReport] r.report.videoAIAnalysis:',
      r.report?.videoAIAnalysis
    )

    if (!r.report?.aiAnalysis) {
      jsLogger.log(
        '[updateSessionReport] aiAnalysis not ready, polling again in',
        INITIAL_POLL_INTERVAL,
        'ms'
      )
      window.setTimeout(updateSessionReport, INITIAL_POLL_INTERVAL)
    } else if (!r.report.videoAIAnalysis || !avatarVideoExists) {
      jsLogger.log(
        '[updateSessionReport] VideoAIAnalysis or user video missing, polling again in',
        SLOW_POLL_INTERVAL,
        'ms'
      )
      window.setTimeout(updateSessionReport, SLOW_POLL_INTERVAL)
    } else {
      jsLogger.log('stopping reports fetching')
    }

    return r
  }

  useEffect(() => {
    const run = async () => {
      const result = await updateSessionReport()
      setIsLoading(false)

      // When animations are disabled, skip to final state
      if (!animationOn) {
        setRevealPhase('videoPlayer')

        setVideoSubsectionIndex(size(result?.report?.videoAIAnalysis))
        return
      }

      // Start AI summary - skip reasoning if data already available
      const hasAiSummary = result?.report?.aiAnalysis?.overallSummary
      setRevealPhase(hasAiSummary ? 'aiSummary' : 'aiSummaryReasoning')
    }
    run()
    return () => {
      if (reportUpdateTimer.current) {
        clearTimeout(reportUpdateTimer.current)
      }
    }
  }, [sessionId, animationOn])

  // Progress through video subsections
  useEffect(() => {
    if (
      revealPhase === 'videoSubsections' &&
      reportResponse?.report?.videoAIAnalysis
    ) {
      if (
        videoSubsectionIndex < size(reportResponse?.report?.videoAIAnalysis)
      ) {
        // Show reasoning for current subsection, then reveal it
        const timer = setTimeout(() => {
          setVideoSubsectionIndex((prev) => prev + 1)
          scrollToBottom()
        }, 3000) // Time for reasoning animation
        return () => clearTimeout(timer)
      } else {
        // All subsections revealed, show video player
        // Don't scroll to bottom - keep focus on video analysis section
        setRevealPhase('videoPlayer')
      }
    }
  }, [revealPhase, reportResponse?.report?.videoAIAnalysis])

  // Only show loading skeleton for initial load
  if (isLoading) {
    return (
      <PageContainer>
        <PageLoadingSkeleton />
      </PageContainer>
    )
  }

  const sessionDate = reportResponse?.createdAt
  const sessionDateConf: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }
  const formattedDate = sessionDate
    ? new Date(sessionDate).toLocaleDateString('en-US', sessionDateConf)
    : null

  // Get analysis items
  const personAnalysis = get(reportResponse?.report?.aiAnalysis?.analysis, 0)

  const locationAnalysis = get(reportResponse?.report?.aiAnalysis?.analysis, 1)

  const renderVideoSubsection = (metric: CriterionAnalysis, index: number) => {
    if (!metric) return null

    const borderColor =
      metric.evaluation === CriterionEvaluation.normal
        ? 'border-l-green-500'
        : metric.evaluation === CriterionEvaluation.suspicious
          ? 'border-l-yellow-500'
          : 'border-l-red-500'

    const scoreColor =
      metric.evaluation === CriterionEvaluation.normal
        ? 'text-green-700'
        : metric.evaluation === CriterionEvaluation.suspicious
          ? 'text-yellow-700'
          : 'text-red-700'

    const bgColor =
      metric.evaluation === CriterionEvaluation.normal
        ? 'bg-green-50'
        : metric.evaluation === CriterionEvaluation.suspicious
          ? 'bg-yellow-50'
          : 'bg-red-50'

    return (
      <div
        key={metric.criterion}
        className={`border-l-4 ${borderColor} ${bgColor} rounded-r-lg p-4 relative animate-fadeIn`}
      >
        <div className='flex items-start justify-between mb-2'>
          <div className='flex-1'>
            <h4 className='font-semibold text-gray-900 mb-1'>
              {startCase(metric.criterion)}
            </h4>
            <span
              className={`inline-block text-base font-bold uppercase ${scoreColor} px-2 py-0.5 rounded`}
              style={{
                backgroundColor:
                  metric.evaluation === CriterionEvaluation.normal
                    ? 'rgba(34, 197, 94, 0.1)'
                    : metric.evaluation === CriterionEvaluation.suspicious
                      ? 'rgba(234, 179, 8, 0.1)'
                      : 'rgba(239, 68, 68, 0.1)'
              }}
            >
              {metric.evaluation === CriterionEvaluation.normal
                ? '✓ Normal'
                : metric.evaluation === CriterionEvaluation.suspicious
                  ? '⚠ Suspicious'
                  : '⚠ High Risk'}
            </span>
          </div>
        </div>
        <p className='text-sm text-gray-700 pr-12'>{metric.observation}</p>
        <span className='absolute bottom-2 right-3 text-xs text-gray-400'>
          {Math.round(metric.confidence)}% conf
        </span>
      </div>
    )
  }

  const primaryVideoUrl = reportResponse?.media?.videos?.avatar?.url
  const photo = reportResponse?.media?.photos?.user?.url
  const videoPoster = photo

  const renderContent = () => {
    if (isJsonView) {
      return <JsonView data={reportResponse} />
    }

    const phaseOrder: RevealPhase[] = [
      'initial',
      'aiSummaryReasoning',
      'aiSummary',
      'personReasoning',
      'personAnalysis',
      'locationReasoning',
      'locationAnalysis',
      'videoReasoning',
      'videoSubsections',
      'videoPlayer',
      'complete'
    ]
    const currentPhaseIndex = phaseOrder.indexOf(revealPhase)
    const isPhaseReached = (phase: RevealPhase) =>
      currentPhaseIndex >= phaseOrder.indexOf(phase)

    return (
      <div ref={contentRef}>
        {/* Session Summary Card + Transcript - Instant */}
        {reportResponse?.report && (
          <SessionSummaryCard
            report={reportResponse.report}
            thumbnailUrl={photo}
          />
        )}

        {/* AI Summary Reasoning */}
        {revealPhase === 'aiSummaryReasoning' &&
          !reportResponse?.report?.videoAIAnalysis && (
            <ReasoningLoader
              statements={reasoningStatements.aiSummary}
              onComplete={() => {
                setRevealPhase('aiSummary')
                scrollToBottom()
              }}
            />
          )}

        {/* AI Summary with Typewriter */}
        {isPhaseReached('aiSummary') &&
          reportResponse?.report?.aiAnalysis?.overallSummary && (
            <div
              className={`bg-white rounded-lg shadow-sm p-6 animate-fadeIn ${
                isPhaseReached('personAnalysis') ? 'mb-6' : 'mb-16'
              }`}
            >
              <div className='flex items-center gap-2 mb-4'>
                <Sparkles className='text-blue-500' size={20} />
                <h2 className='text-lg font-semibold text-gray-900'>Summary</h2>
              </div>
              <div className='text-gray-700 break-words'>
                {isPhaseReached('personReasoning') ? (
                  <span>{reportResponse.report.aiAnalysis.overallSummary}</span>
                ) : (
                  <Typewriter
                    options={{ cursor: '●', delay: TYPEWRITER_DELAY }}
                    onInit={(typewriter) => {
                      // Start scroll interval during typing
                      const scrollInterval = setInterval(() => {
                        scrollToBottom()
                      }, TYPEWRITER_SCROLL_INTERVAL)

                      typewriter
                        .callFunction((state) => {
                          if (state.elements.cursor) {
                            state.elements.cursor.style.color =
                              TYPEWRITER_CURSOR_COLOR
                          }
                        })
                        .typeString(
                          reportResponse?.report?.aiAnalysis?.overallSummary ||
                            ''
                        )
                        .start()
                        .callFunction(() => {
                          clearInterval(scrollInterval)

                          setRevealPhase(
                            personAnalysis
                              ? 'personAnalysis'
                              : 'personReasoning'
                          )
                          scrollToBottom()
                          const cursor = document.querySelector(
                            '.Typewriter__cursor'
                          )
                          if (cursor) {
                            ;(cursor as HTMLElement).style.display = 'none'
                          }
                        })
                    }}
                  />
                )}
              </div>
            </div>
          )}

        {/* Person Analysis Reasoning */}
        {revealPhase === 'personReasoning' && personAnalysis && (
          <ReasoningLoader
            statements={reasoningStatements.personAnalysis}
            onComplete={() => {
              setRevealPhase('personAnalysis')
              scrollToBottom()
            }}
          />
        )}

        {/* Person Analysis */}
        {isPhaseReached('personAnalysis') && personAnalysis && (
          <div className='bg-white rounded-lg shadow-sm p-6 mb-6 animate-fadeIn'>
            <div className='flex items-center gap-2 mb-3'>
              <User size={16} className='text-blue-500' />
              <h3 className='font-semibold text-gray-900'>
                {personAnalysis.title}
              </h3>
            </div>
            <div className='text-sm text-gray-700 mb-3'>
              {personAnalysis.shortDescription}
            </div>
            {personAnalysis.longDescription && (
              <div className='bg-gray-900 rounded-lg p-4 mt-3'>
                <p className='text-gray-300 text-sm whitespace-pre-wrap break-words'>
                  {personAnalysis.longDescription}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Trigger location analysis after person analysis */}
        {revealPhase === 'personAnalysis' &&
          (() => {
            setTimeout(() => {
              if (locationAnalysis) {
                // Skip reasoning - show location immediately
                setRevealPhase('locationAnalysis')
                setTimeout(() => scrollToBottom(), 100)
              } else {
                // No location data, skip to video
                const hasVideoData = reportResponse?.report?.videoAIAnalysis
                if (hasVideoData) {
                  setRevealPhase('videoSubsections')
                  setVideoSubsectionIndex(
                    size(reportResponse?.report?.videoAIAnalysis)
                  )
                  // Don't scroll - keep focus on video analysis
                } else {
                  setRevealPhase('videoReasoning')
                  setTimeout(() => scrollToBottom(), 100)
                }
              }
            }, 1000)
            return null
          })()}

        {/* Location Analysis Reasoning */}
        {revealPhase === 'locationReasoning' && locationAnalysis && (
          <ReasoningLoader
            statements={reasoningStatements.locationAnalysis}
            onComplete={() => {
              setRevealPhase('locationAnalysis')
              scrollToBottom()
            }}
          />
        )}

        {/* Skip to location reasoning if no person analysis */}
        {revealPhase === 'personReasoning' &&
          !personAnalysis &&
          (() => {
            setTimeout(() => setRevealPhase('locationReasoning'), 0)
            return null
          })()}

        {/* Location Analysis */}
        {isPhaseReached('locationAnalysis') && locationAnalysis && (
          <div className='bg-white rounded-lg shadow-sm p-6 mb-6 animate-fadeIn'>
            <div className='flex items-center gap-2 mb-3'>
              <MapPin size={16} className='text-blue-500' />
              <h3 className='font-semibold text-gray-900'>
                {locationAnalysis.title}
              </h3>
            </div>
            <div className='text-sm text-gray-700 mb-3'>
              {locationAnalysis.shortDescription}
            </div>
            {locationAnalysis.longDescription && (
              <div className='bg-gray-900 rounded-lg p-4 mt-3'>
                <p className='text-gray-300 text-sm whitespace-pre-wrap break-words'>
                  {locationAnalysis.longDescription}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Trigger video analysis after location */}
        {revealPhase === 'locationAnalysis' &&
          (() => {
            // Skip reasoning if video analysis data already exists
            const hasVideoData = reportResponse?.report?.videoAIAnalysis
            setTimeout(() => {
              if (hasVideoData) {
                setRevealPhase('videoSubsections')
                setVideoSubsectionIndex(
                  size(reportResponse?.report?.videoAIAnalysis)
                )
                // Don't scroll - keep focus on video analysis
              } else {
                setRevealPhase('videoReasoning')
                setTimeout(() => scrollToBottom(), 100)
              }
            }, 1000)
            return null
          })()}

        {/* Conversation Analysis - appears before Detected Signals */}
        {isPhaseReached('locationAnalysis') &&
          reportResponse?.semanticRiskAnalysis && (
            <div className='bg-white rounded-lg shadow-sm p-6 mb-6 animate-fadeIn'>
              <div className='flex items-center justify-between mb-4'>
                <div className='flex items-center gap-2'>
                  <MessageSquareText className='text-blue-500' size={20} />
                  <h2 className='text-lg font-semibold text-gray-900'>
                    Conversation Analysis
                  </h2>
                </div>
                {/* Risk Level Badge */}
                {reportResponse.semanticRiskAnalysis.riskLevel && (
                  <div
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                      reportResponse.semanticRiskAnalysis.riskLevel === 'HIGH'
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {reportResponse.semanticRiskAnalysis.riskLevel === 'HIGH'
                      ? 'High Risk'
                      : 'Low Risk'}
                  </div>
                )}
              </div>

              {reportResponse.semanticRiskAnalysis.description && (
                <div className='bg-gray-900 rounded-lg p-4'>
                  <p className='text-gray-300 text-sm whitespace-pre-wrap break-words'>
                    {reportResponse.semanticRiskAnalysis.description}
                  </p>
                </div>
              )}
            </div>
          )}

        {/* Detected Signals Section */}
        {isPhaseReached('videoReasoning') && (
          <div className='bg-white rounded-lg shadow-sm p-6 mb-16'>
            <div className='flex items-center gap-2 mb-4'>
              <Video className='text-blue-500' size={20} />
              <h2 className='text-lg font-semibold text-gray-900'>
                Detected Signals
              </h2>
            </div>

            {/* Meta Score Summary - appears when video analysis is ready */}
            {isPhaseReached('videoSubsections') && reportResponse && (
              <MetaScoreSummary
                providedUserDetected={reportResponse.providedUserDetected}
                videoAIAnalysis={reportResponse.report?.videoAIAnalysis}
                semanticRiskLevel={
                  reportResponse.semanticRiskAnalysis?.riskLevel
                }
              />
            )}

            {/* Video Reasoning */}
            {revealPhase === 'videoReasoning' && (
              <ReasoningLoader
                statements={[
                  ...reasoningStatements.videoAnalysis.general,
                  ...reasoningStatements.videoAnalysis.extended
                ]}
                intervalMs={1500}
                minStatements={5}
                shouldComplete={!!reportResponse?.report?.videoAIAnalysis}
                onComplete={() => {
                  if (reportResponse?.report?.videoAIAnalysis) {
                    setRevealPhase('videoSubsections')
                    // Show all subsections immediately when data is available
                    setVideoSubsectionIndex(
                      size(reportResponse?.report?.videoAIAnalysis)
                    )
                    // Don't scroll - keep focus on video analysis
                  }
                }}
              />
            )}

            {/* Video Subsections */}
            {isPhaseReached('videoSubsections') &&
              reportResponse?.report?.videoAIAnalysis && (
                <div className='space-y-3 mt-4'>
                  {map(
                    reportResponse?.report?.videoAIAnalysis,
                    renderVideoSubsection
                  )}
                </div>
              )}
          </div>
        )}

        {/* Video Player */}
        {isPhaseReached('videoPlayer') && (
          <div className='bg-white rounded-lg shadow-sm p-6 mb-16'>
            <div className='flex items-center gap-2 mb-4'>
              <Video className='text-blue-500' size={20} />
              <h2 className='text-lg font-semibold text-gray-900'>
                Session Playback
              </h2>
            </div>
            {!primaryVideoUrl && (
              <ReasoningLoader
                statements={reasoningStatements.sessionRecording}
                intervalMs={1500}
                minStatements={5}
                shouldComplete={!!primaryVideoUrl}
                onComplete={() => {
                  // if (reportResponse?.report?.videoAIAnalysis) {
                  //   setRevealPhase('videoSubsections')
                  //   // Show all subsections immediately when data is available
                  //   setVideoSubsectionIndex(videoSubsections.length)
                  //   // Don't scroll - keep focus on video analysis
                  // }
                }}
              />
            )}
            {primaryVideoUrl && (
              <SessionVideo
                primaryVideoUrl={primaryVideoUrl}
                videoPoster={videoPoster}
                setVideoDuration={setVideoDuration}
              />
            )}
          </div>
        )}

        {/* Transcript - show after video analysis */}
        {isPhaseReached('videoPlayer') &&
          reportResponse?.report?.transcript && (
            <TranscriptSection
              transcript={reportResponse.report.transcript}
              language={reportResponse.report.lang}
            />
          )}

        {/* Bottom spacer - always reserve space during loading phases */}
        {!isPhaseReached('videoPlayer') && <div className='h-24' />}

        {/* Final state: Static icon (left) and Share button (right) */}
        {isPhaseReached('videoPlayer') && (
          <div className='flex justify-between items-center mb-16 pb-8 animate-fadeIn'>
            <div className='w-8 h-8' style={{ color: FACESIGN_GREEN }}>
              <FacesignIcon className='w-full h-full' />
            </div>
            <ShareButton sessionId={sessionId} />
          </div>
        )}
      </div>
    )
  }

  return (
    <PageContainer contentClassName='h-[100dvh] bg-gray-50 pt-[env(safe-area-inset-top)]'>
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className='h-[calc(100dvh-env(safe-area-inset-top))] overflow-y-auto overflow-x-hidden'
      >
        <div className='max-w-4xl mx-auto px-3 sm:px-4 py-6 sm:py-8'>
          {/* Scroll to bottom button */}
          {isUserScrolledUp && !isJsonView && (
            <button
              onClick={forceScrollToBottom}
              className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-50 p-3 rounded-full shadow-lg hover:shadow-xl transition-all ${
                hasNewContentBelow
                  ? 'ring-2 ring-green-400 ring-opacity-75'
                  : ''
              }`}
              style={
                !isLoadingComplete
                  ? {
                      background: SHIMMER_GRADIENT,
                      backgroundSize: '200% 100%',
                      animation: `shimmer ${SHIMMER_DURATION_S}s ease-in-out infinite`
                    }
                  : {
                      background: '#ffffff'
                    }
              }
              aria-label='Scroll to bottom'
            >
              <ChevronDown size={24} className='text-gray-900' />
            </button>
          )}
          {/* Header - Always visible */}
          <div className='mb-4 sm:mb-8 flex flex-row justify-between items-start gap-4'>
            <div className='min-w-0 flex-1'>
              <div className='flex items-center gap-3 flex-wrap'>
                <h1 className='text-xl sm:text-2xl font-bold text-gray-900'>
                  Session Report
                </h1>
                {/* Status badge - shows when results are ready */}
                {headerLevel && <StatusBadge level={headerLevel} />}
              </div>
              {formattedDate && (
                <p className='text-sm text-gray-500 mt-1'>
                  {formattedDate}
                  {videoDuration && videoDuration > 0 && (
                    <span>
                      {' '}
                      • {Math.floor(videoDuration / 60)}m{' '}
                      {Math.floor(videoDuration % 60)}s
                    </span>
                  )}
                </p>
              )}
            </div>
            <div className='flex items-center gap-2 flex-shrink-0'>
              {isJsonView ? (
                <button
                  onClick={() => handleViewToggle(false)}
                  className='flex items-center gap-1 px-2 py-2 text-xs font-medium text-blue-600 hover:text-blue-700'
                >
                  ← Back to Report
                </button>
              ) : null}
              <ViewToggle isJsonView={isJsonView} onToggle={handleViewToggle} />
            </div>
          </div>

          {/* Content based on view mode */}
          {renderContent()}
        </div>
      </div>
    </PageContainer>
  )
}

export default SessionReport
